import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useGlobalState } from '@src/context/GlobalStateContext';
import { useProjectsQuery } from '@src/services/queries/useProjectQuery';
import { EmissionsSource } from '@src/types';
import { formatNumber } from '@src/utils/format-number';
import { Profile } from '@src/utils/state-profile';
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import PolicyRefunds from '../Setting/policyRefunds';
import ChillPay from './Chilpay';
import './payment.css';
const merchant_id = process.env.REACT_APP_MERCHANT_ID;
const paymentUrl = process.env.REACT_APP_PAYMENT_URL;

export default function Payment({ eventId }: { eventId: number }) {
    const navigate = useNavigate();
    const projectsQuery = useProjectsQuery(true);

    const profile = Profile();
    const { t, i18n } = useTranslation();
    const { gasEmissions, projectSelect } = useGlobalState();

    const [openConfirmPaid, setOpenConfirmPaid] = useState<boolean>(false);
    const [openRefundPolicy, setOpenRefundPolicy] = useState<boolean>(false);
    const [isDisableConfirm, setIsDisableConfirm] = useState<boolean>(true);
    const [projectName, setProjectName] = useState<string>('');

    useEffect(() => {
        if (projectsQuery?.data && projectSelect) {
            const { data } = projectsQuery?.data;
            if (data.data.length > 0) {
                const resultFilter = data.data.find((d: any) => d.id === parseInt(projectSelect.id));
                setProjectName(i18n.language === 'th' ? resultFilter.nameTH : resultFilter.nameEN);
            }
        }
    }, [projectsQuery, projectSelect, i18n.language]);

    function handleClickPaid(e: MouseEvent<HTMLElement>) { }
    function handleClickUnPaid(e: MouseEvent<HTMLElement>) {
        navigate('/');
    }

    function handleCloseConfirmPaid() {
        setOpenConfirmPaid(false);
    }

    function handleCloseRefundPolicy() {
        setOpenRefundPolicy(false);
    }

    function handleOpenRefundPolicy() {
        setOpenRefundPolicy(true);
    }

    function handleClickAccpetConsent(e: ChangeEvent<HTMLInputElement>) {
        let checkedStatus = e.target.checked;
        setIsDisableConfirm(!checkedStatus);
    }

    const { totalCarbonFootprint } = useMemo(() => {
        return (gasEmissions || []).reduce(
            (totals: any, source: EmissionsSource) => {
                totals.totalCarbonFootprint += source.total / 1000;
                return totals;
            },
            { totalCarbonFootprint: 0 },
        );
    }, [gasEmissions]);

    const projectPrice = projectSelect?.price || 0;
    const totalPricePaid = Math.ceil(totalCarbonFootprint) * projectPrice;

    const initialAmount = totalPricePaid;
    const serviceFeeRate = 3.25 / 100;

    // คำนวณค่าบริการ
    const feeAmount = initialAmount * serviceFeeRate;
    const totalAmount = initialAmount + feeAmount;

    return (
        <div className="w-full max-w-4xl mx-auto mt-8 md:mt-16 lg:mt-28 px-1 md:px-0">
            <div className="box rounded-3xl md:rounded-[90px] p-4 md:p-6 lg:p-8">
                <div className="columns is-mobile">
                    <div className="column w-full px-2 md:px-4  " >
                        <div className='flex justify-center'>
                            <Box
                                display="block"
                                alignItems="center"
                                className="border-4 border-[#B5D224] rounded-2xl p-4 md:p-6 text-center"
                                sx={{
                                    maxWidth: {
                                        md: '90vh',
                                        xs: '100%'
                                    }
                                }}
                            >
                                <div className="column">
                                    <h1 className="content-sub-header text-sm md:text-base lg:text-lg">{t('PAID_PAGE.TITLE')}</h1>
                                    <h1 className="content-header text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-2">
                                        {Math.ceil(totalCarbonFootprint)}
                                        {/* {formatNumber(totalCarbonFootprint, 2)} */}
                                    </h1>
                                    <h1 className="content-header-s text-[#26B3F0] text-base md:text-lg lg:text-xl mt-2 truncate">
                                        {projectName}
                                    </h1>
                                </div>
                            </Box>
                        </div>
                        <div className="overflow-hidden w-full mt-4 md:mt-6">
                            {merchant_id && paymentUrl && totalPricePaid > 0 && (
                                <ChillPay
                                    customerID={profile.mapId}
                                    totalPaid={totalAmount} // totalAmount paid
                                    paymentURL={paymentUrl}
                                    merchantID={merchant_id as string}
                                    eventId={eventId}
                                    mobileNo={profile.mobile ?? '0880000000'}
                                />
                            )}
                        </div>
                        <div className="space-y-2 md:space-y-3 mt-4 md:mt-6 bg-gray-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center text-lg md:text-xl lg:text-2xl">
                                <div className="text-base md:text-lg lg:text-xl">{t('PAID_PAGE.TOTAL')}</div>
                                <div className="font-medium">{'฿' + formatNumber(initialAmount)}</div>
                            </div>
                            <div className="flex justify-between items-center text-lg md:text-xl lg:text-2xl">
                                <div className="text-base md:text-lg lg:text-xl">{t('PAID_PAGE.SERVICE_FEE')}</div>
                                <div className="font-normal">{'฿' + formatNumber(feeAmount, 2)}</div>
                            </div>
                            <div className="border-t pt-2 md:pt-3">
                                <div className="flex justify-between items-center mb-4 text-lg md:text-xl lg:text-2xl">
                                    <div className="text-base md:text-lg lg:text-xl text-black font-semibold">{t('PAID_PAGE.AMOUNT_TOTAL')}</div>
                                    <div className="text-right font-bold">
                                        <div className="text-lg md:text-xl lg:text-2xl">{'฿' + formatNumber(totalAmount, 2)}</div>
                                        <div className="text-xs md:text-sm font-normal text-gray-600">{`฿ ${formatNumber(projectPrice)}/tCO2eq`}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 md:mt-8 mb-16">
                            <div className="w-full md:w-1/2 md:ml-auto">
                                <button
                                    className="button is-large is-fullwidth is-light App w-full h-14 md:h-16 lg:h-[70px] text-base md:text-lg"
                                    onClick={handleClickUnPaid}
                                >
                                    {t('PAID_PAGE.BUTTONS.PENDING')}
                                </button>
                            </div>
                        </div>
                        <br />
                    </div>
                </div>
                <Dialog
                    open={openConfirmPaid}
                    onClose={handleCloseConfirmPaid}
                    fullWidth
                    maxWidth="sm"
                    aria-labelledby="scroll-dialog-title"
                    aria-describedby="scroll-dialog-description"
                    PaperProps={{
                        style: {
                            borderRadius: '15px',
                            margin: '16px',
                        },
                    }}
                >
                    <DialogContent dividers={false} className="p-4 md:p-6">
                        <div className="text-center mb-4">
                            <h3 className="text-lg md:text-xl font-bold">{t('PAID_PAGE.CONFIRM_DIAG.TITLE')}</h3>
                        </div>
                        <div className="mb-6">
                            <p className="text-left text-sm md:text-base">{t('PAID_PAGE.CONFIRM_DIAG.CONTENT')}</p>
                        </div>
                        <div className="flex items-start gap-3">
                            <input
                                className="mt-1 w-5 h-5 flex-shrink-0"
                                type="checkbox"
                                onChange={handleClickAccpetConsent}
                            />
                            <p className="text-left text-xs md:text-sm leading-relaxed">
                                {t('PAID_PAGE.CONFIRM_DIAG.CONSENT')}
                                {/* eslint-disable jsx-a11y/anchor-is-valid  */}
                                <a
                                    className="text-blue-600 underline cursor-pointer ml-1"
                                    onClick={handleOpenRefundPolicy}
                                >
                                    {t('PAID_PAGE.CONFIRM_DIAG.BUTTON')}
                                </a>
                            </p>
                        </div>
                    </DialogContent>
                    <DialogActions className="p-4 md:p-6 pt-0">
                        <div className="w-full flex flex-col gap-3">
                            <button
                                className="button is-info is-fullwidth App w-full h-12 md:h-14 text-sm md:text-base"
                                type="button"
                                onClick={handleClickPaid}
                                disabled={isDisableConfirm}
                            >
                                {t('BUTTONS.COMFIRM')}
                            </button>
                            <button
                                className="button is-light is-fullwidth App w-full h-12 md:h-14 text-sm md:text-base"
                                type="submit"
                                onClick={handleCloseConfirmPaid}
                            >
                                {t('BUTTONS.CANCEL')}
                            </button>
                        </div>
                    </DialogActions>
                </Dialog>
                <PolicyRefunds open={openRefundPolicy} handleClose={handleCloseRefundPolicy} />
            </div>
        </div>
    );
}
